import { useId } from 'react'

import { Icon } from '@/components/ui/Icon'
import { LABEL } from '@/components/ui/formFields'
import { cn } from '@/lib/cn'
import { Check } from '@/lib/icons'

interface CheckChipGroupProps {
  legend: string
  hint?: string
  options: readonly string[]
  selected: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

/**
 * A multi-select as a `<fieldset>` of checkbox chips. Selected state shows a
 * check icon (not colour alone — fails 1.4.1 / High Contrast otherwise). The
 * `<input>` is visually hidden, so `has-[:focus-visible]` on the label
 * reproduces the focus ring the global rule would draw on the input.
 */
export function CheckChipGroup({
  legend,
  hint,
  options,
  selected,
  onChange,
  disabled,
}: CheckChipGroupProps) {
  const hintId = useId()

  return (
    <fieldset
      disabled={disabled}
      aria-describedby={hint ? hintId : undefined}
      className="space-y-3"
    >
      <legend className={LABEL}>{legend}</legend>
      {hint && (
        <p id={hintId} className="font-body text-body-sm text-fg-muted">
          {hint}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isOn = selected.includes(option)
          return (
            <label
              key={option}
              className={cn(
                'font-body text-label-caps inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-full border px-4 uppercase transition-colors',
                'has-[:focus-visible]:outline-primary has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2',
                isOn
                  ? 'border-primary-container/30 bg-primary-container/15 text-primary'
                  : 'border-glass-border text-fg-muted hover:text-fg bg-white/5',
              )}
            >
              <input
                type="checkbox"
                checked={isOn}
                onChange={() => {
                  onChange(
                    isOn
                      ? selected.filter((item) => item !== option)
                      : [...selected, option],
                  )
                }}
                className="sr-only"
              />
              {isOn && <Icon icon={Check} className="size-3.5" />}
              {option}
            </label>
          )
        })}
      </div>
      <p className="font-body text-label-sm text-fg-muted" aria-live="polite">
        {selected.length === 0
          ? 'None selected'
          : `${selected.length} selected`}
      </p>
    </fieldset>
  )
}
